import {
  Header,
  Sidebar,
  SidebarFooter,
  SidebarItem,
  SidebarPageWrapper,
} from '@trueprodigy/components';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import styled from 'styled-components';
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import * as globalActions from '@actions/global';
import { canViewSubject, canExecuteSubject } from '@config/permissions/Can';
import {
  isSidebarVisibleSelector,
  userRightsSelector,
} from '@selectors/global';
import ability, { defineRules } from '@config/permissions/ability';
import SidebarRdsNameWidget from '@components/Sidebar/SidebarRdsNameWidget';
import SidebarCaseSettingWidget from '@components/Sidebar/SidebarCaseSettingWidget';
import SidebarAccountSettingsWidget from '@components/Sidebar/SidebarAccountSettingsWidget';
import rocketIcon from '@components/Sidebar/assets/rocket-icon.png';
import HeaderWrapper from '@components/HeaderWrapper';

import appraisalAdminLogo from '../assets/logo.png';
import {
  parseHeaderTitle,
  appRoutes,
  bppPricingMaintenanceRoutes,
  improvementPricingMaintenanceRoutes,
  landPricingMaintenanceRoutes,
  prefix,
} from '../routes';

const PageWrapper = ({
  children,
  toggleSidebar,
  isSidebarVisible,
  userRights,
  location,
}) => {
  useEffect(() => {
    if (userRights && userRights.toJS() && userRights.toJS().length) {
      const rules = defineRules(userRights.toJS());
      ability.update(rules);
    }
  }, [userRights]);

  useEffect(() => {
    const isiPad = /iPad/i.test(navigator.userAgent);
    if (isSidebarVisible && isiPad) {
      toggleSidebar();
    }
  }, [location.pathname]);

  return (
    <>
      <Helmet>
        <title>Appraisal Admin</title>
      </Helmet>

      <Sidebar
        isSidebarVisible={isSidebarVisible}
        logo={appraisalAdminLogo}
        sidebarFooter={
          <SidebarFooter>
            <SidebarRdsNameWidget />
            <SidebarCaseSettingWidget />
            <SidebarAccountSettingsWidget />
          </SidebarFooter>
        }
      >
        <SidebarItem icon={rocketIcon} link="/" title="Prodigy App Launcher" />
        <PageWrapper.MenuTitle>App Menu</PageWrapper.MenuTitle>
        <SidebarItem link={prefix} title="Summary" />
        <SidebarItem link={`${prefix}/office-info`} title="Office Info" />
        {canViewSubject('AppraisalUIMaintenance') && (
          <SidebarItem isOpen nested title="UI Maintenance">
            {Object.entries(appRoutes).map(([link, { menu }]) => (
              <SidebarItem key={link} link={link} title={menu} />
            ))}
          </SidebarItem>
        )}
        <SidebarItem title="TIRZ Settings" link={`${prefix}/tirz`} />
        {canViewSubject('AppraisalCodefileMaintenance') && (
          <SidebarItem
            link={prefix + '/codefile-maintenance'}
            title="Codefile Maintenance"
          />
        )}
        {/* <SidebarItem
          link={prefix + '/electronic-delivery-request-configuration'}
          title="Electronic Delivery Request Configuration"
        /> */}
        {canViewSubject('AppraisalMarketAreaMaintenance') && (
          <SidebarItem title="Market Area" link={`${prefix}/market-area`} />
        )}
        {canViewSubject('AppraisalAbstractSudvidisionMaintenance') && (
          <SidebarItem
            title="Abstract Subdivision"
            link={`${prefix}/abstract-subdivision`}
          />
        )}
        {canViewSubject('AppraisalWalkthruSettings') && (
          <SidebarItem
            link={prefix + '/walkthrough/settings'}
            title="Walkthru Default Settings"
          />
        )}
        <SidebarItem isOpen nested title="Land Pricing Maintenance">
          {Object.entries(landPricingMaintenanceRoutes)
            .filter(
              ([, { viewPermissionSubject }]) =>
                !viewPermissionSubject || canViewSubject(viewPermissionSubject),
            )
            .map(([link, { menu }]) => (
              <SidebarItem key={link} link={link} title={menu} />
            ))}
        </SidebarItem>
        <SidebarItem isOpen nested title="Improvement Pricing Maintenance">
          {Object.entries(improvementPricingMaintenanceRoutes)
            .filter(
              ([, { viewPermissionSubject }]) =>
                !viewPermissionSubject || canViewSubject(viewPermissionSubject),
            )
            .map(([link, { menu }]) => (
              <SidebarItem key={link} link={link} title={menu} />
            ))}
        </SidebarItem>
        <SidebarItem isOpen nested title="BPP Pricing Maintenance">
          {Object.entries(bppPricingMaintenanceRoutes)
            .filter(
              ([, { viewPermissionSubject }]) =>
                !viewPermissionSubject || canViewSubject(viewPermissionSubject),
            )
            .map(([link, { menu }]) => (
              <SidebarItem key={link} link={link} title={menu} />
            ))}
        </SidebarItem>
        <SidebarItem
          title="BPP Rendition Processing Configuration"
          link={`${prefix}/bpp-rendition-configuration`}
        />
        {canViewSubject('AppraisalAppraiserSettings') && (
          <SidebarItem
            title="Appraiser Settings"
            link={`${prefix}/appraiser-settings`}
          />
        )}
        {canExecuteSubject('AppraisalCacheRebuild') && (
          <SidebarItem title="Cache" link={`${prefix}/cache`} />
        )}
        <SidebarItem title="Income Models" link={`${prefix}/income-models`} />
        <SidebarItem
          title="Letter Case Setting"
          link={`${prefix}/letter-case-setting`}
        />
        <SidebarItem title="Data Entry" link={`${prefix}/data-entries`} />
      </Sidebar>
      <PageWrapper.ContentWrapper>
        <SidebarPageWrapper isSidebarVisible={isSidebarVisible}>
          <HeaderWrapper>
            <Header
              title={parseHeaderTitle(location.pathname)}
              toggleSidebar={toggleSidebar}
            />
          </HeaderWrapper>
          <div>{children}</div>
        </SidebarPageWrapper>
      </PageWrapper.ContentWrapper>
    </>
  );
};

PageWrapper.propTypes = {
  children: PropTypes.any,
  toggleSidebar: PropTypes.func,
  isSidebarVisible: PropTypes.bool,
  userRights: ImmutablePropTypes.list,
  location: PropTypes.any,
  history: PropTypes.object.isRequired,
};

PageWrapper.MenuTitle = styled.div`
  height: 35px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.4);
  background-color: #fff;
  color: #2f3e9e;
  font-size: 14px;
  padding: 8px 26px;
  font-weight: normal;
`;

PageWrapper.ContentWrapper = styled.div`
  > div {
    margin-top: -67px;
    min-height: 100vh;
    background-color: #f2f4f6;
  }
`;

const mapStateToProps = (state) => ({
  isSidebarVisible: isSidebarVisibleSelector(state),
  userRights: userRightsSelector(state),
});

const mapDispatchToProps = {
  toggleSidebar: globalActions.toggleSidebar,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(PageWrapper),
);
